// Seasonal particle mix — what the ambient particle systems do per season.
//
// The calendar look lives in seasons.ts; this module derives the *particle*
// profile from the same seasonPhase(). Two systems share the sky:
//  - motes: the warm additive dust/pollen drift (existing AmbientParticles
//    behaviour), strongest in spring/summer, nearly gone in winter.
//  - fall: gravity particles — autumn leaves that turn into snow across the
//    autumn→winter transition (colour, size, fall speed and sway all lerp).

import type { Season } from '../sim/gameTime.js';
import { seasonPhase } from './seasons.js';

/** RGB 0..1, parsed from hex without pulling THREE into the tests. */
function rgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Gravity-particle profile for a moment in the calendar. */
export interface FallProfile {
  readonly opacity: number;
  readonly color: readonly [number, number, number];
  readonly size: number;
  /** Downward drift per frame at 60fps (world units). */
  readonly fallSpeed: number;
  /** Horizontal sway amplitude (world units). */
  readonly sway: number;
}

export interface ParticleMix {
  readonly motesOpacity: number;
  readonly fall: FallProfile;
}

const MOTES_OPACITY: Record<Season, number> = {
  spring: 0.6,
  summer: 0.6,
  autumn: 0.35,
  winter: 0.08,
};

const FALL: Record<Season, FallProfile> = {
  spring: { opacity: 0, color: rgb('#eef4f8'), size: 0.07, fallSpeed: 0.004, sway: 0.008 },
  summer: { opacity: 0, color: rgb('#eef4f8'), size: 0.07, fallSpeed: 0.004, sway: 0.008 },
  autumn: { opacity: 0.5, color: rgb('#c86a28'), size: 0.12, fallSpeed: 0.012, sway: 0.02 },
  winter: { opacity: 0.55, color: rgb('#eef4f8'), size: 0.07, fallSpeed: 0.004, sway: 0.008 },
};

function blendFall(a: FallProfile, b: FallProfile, t: number): FallProfile {
  return {
    opacity: a.opacity + (b.opacity - a.opacity) * t,
    color: [
      a.color[0] + (b.color[0] - a.color[0]) * t,
      a.color[1] + (b.color[1] - a.color[1]) * t,
      a.color[2] + (b.color[2] - a.color[2]) * t,
    ],
    size: a.size + (b.size - a.size) * t,
    fallSpeed: a.fallSpeed + (b.fallSpeed - a.fallSpeed) * t,
    sway: a.sway + (b.sway - a.sway) * t,
  };
}

/**
 * The particle mix for a sim day, blended across the season-phase transition
 * window (so leaves turn to snow as winter fades in, not at a snap).
 */
export function seasonParticleMix(day: number): ParticleMix {
  const { season, next, t } = seasonPhase(day);
  return {
    motesOpacity: MOTES_OPACITY[season] + (MOTES_OPACITY[next] - MOTES_OPACITY[season]) * t,
    fall: blendFall(FALL[season]!, FALL[next]!, t),
  };
}
